import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Navbar } from "@/components/Navbar";
import { Footer } from "@/components/Footer";
import { Gift, Eye, ShoppingBag, Wallet, Copy, Check, MessageCircle } from "lucide-react";
import { buildShareUrl } from "@/utils/referral";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const StatusMessage = ({ stats }) => {
  if (stats.pending_mxn > 0) {
    return <p className="text-sm text-amber-400 mb-6">Tienes ${stats.pending_mxn} MXN pendientes de pago. Escribenos por WhatsApp para cobrar.</p>;
  }
  if (stats.sales > 0) {
    return <p className="text-sm text-[#00FF66] mb-6">Todo pagado. Sigue compartiendo para ganar mas.</p>;
  }
  return <p className="text-sm text-[#A0A0A0] mb-6">Aun no tienes compras. Comparte tu enlace para empezar a ganar.</p>;
};

const NotFound = () => (
  <div className="glass-card p-8 text-center">
    <p className="text-[#A0A0A0] mb-4">No encontramos este codigo de referido.</p>
    <Link to="/" className="text-[#00FF66] hover:underline">Volver al inicio</Link>
  </div>
);

const Loading = () => (
  <div className="flex justify-center py-20"><div className="w-10 h-10 border-4 border-[#B026FF] border-t-transparent rounded-full animate-spin" /></div>
);

export default function ReferralStatusPage() {
  const { code } = useParams();
  const [stats, setStats] = useState(null);
  const [error, setError] = useState(false);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    axios.get(`${API}/referrals/${code}/stats`)
      .then(res => setStats(res.data))
      .catch(() => setError(true));
  }, [code]);

  const shareUrl = stats ? buildShareUrl(stats.code) : "";

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast.error("No se pudo copiar. Selecciona el enlace y copialo manualmente.");
    }
  };

  return (
    <div className="min-h-screen bg-[#050505]" data-testid="referral-status-page">
      <Navbar />
      <section className="py-12 sm:py-20 px-4">
        <div className="max-w-2xl mx-auto">
          {error && <NotFound />}
          {!error && !stats && <Loading />}
          {!error && stats && (
            <div className="glass-card p-6 sm:p-10 border-[#B026FF]/40 text-center">
              <div className="w-16 h-16 rounded-full bg-[#B026FF]/10 flex items-center justify-center mx-auto mb-4">
                <Gift className="w-8 h-8 text-[#B026FF]" />
              </div>
              <p className="text-[#A0A0A0] text-base">Hola, <span className="text-white font-semibold">{stats.name}</span></p>
              <h1 className="font-oswald text-4xl sm:text-5xl font-bold text-white uppercase mt-2 mb-1">Has ganado</h1>
              <p className="font-oswald text-6xl sm:text-7xl font-bold text-[#00FF66]" data-testid="referral-earned">${stats.earned_mxn}</p>
              <p className="text-[#A0A0A0] text-sm mb-8">MXN en total con tu codigo <span className="text-white font-mono tracking-widest">{stats.code}</span></p>

              <div className="grid grid-cols-3 gap-3 mb-8">
                <div className="bg-[#050505] rounded-xl p-4 border border-white/5">
                  <Eye className="w-5 h-5 text-cyan-400 mx-auto mb-1" />
                  <p className="font-oswald text-2xl font-bold text-white" data-testid="referral-visits">{stats.visits}</p>
                  <p className="text-xs text-[#A0A0A0]">Visitas</p>
                </div>
                <div className="bg-[#050505] rounded-xl p-4 border border-white/5">
                  <ShoppingBag className="w-5 h-5 text-[#00FF66] mx-auto mb-1" />
                  <p className="font-oswald text-2xl font-bold text-white" data-testid="referral-sales">{stats.sales}</p>
                  <p className="text-xs text-[#A0A0A0]">Compras</p>
                </div>
                <div className="bg-[#050505] rounded-xl p-4 border border-white/5">
                  <Wallet className="w-5 h-5 text-amber-400 mx-auto mb-1" />
                  <p className="font-oswald text-2xl font-bold text-white" data-testid="referral-pending">${stats.pending_mxn}</p>
                  <p className="text-xs text-[#A0A0A0]">Por cobrar</p>
                </div>
              </div>

              <StatusMessage stats={stats} />

              <p className="text-xs text-[#A0A0A0] break-all mb-3">{shareUrl}</p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <button onClick={() => window.open(`https://wa.me/?text=${encodeURIComponent(`Mira esta tienda de ropa original americana a precios bajos: ${shareUrl}`)}`, "_blank")} className="bg-[#25D366] text-white py-3 rounded-xl font-bold flex items-center justify-center gap-2" data-testid="referral-status-share-btn">
                  <MessageCircle className="w-5 h-5" /> Compartir por WhatsApp
                </button>
                <button onClick={handleCopy} className="bg-[#121212] border border-white/10 text-white py-3 rounded-xl font-bold flex items-center justify-center gap-2 hover:border-[#00FF66]" data-testid="referral-status-copy-btn">
                  {copied ? <Check className="w-5 h-5 text-[#00FF66]" /> : <Copy className="w-5 h-5" />} Copiar enlace
                </button>
              </div>
            </div>
          )}
        </div>
      </section>
      <Footer />
    </div>
  );
}
